import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { ArrowLeft, Save } from "lucide-react";

export default function ChapterEdit() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: chapter, isLoading } = useQuery({
    queryKey: ["admin-chapter", id],
    queryFn: () => api.get("/admin/chapters").then((chs) => chs.find((c) => c.id === id)),
  });

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (chapter && !form) {
      setForm({
        name: chapter.name || "",
        city: chapter.city || "",
        country: chapter.country || "",
        description: chapter.description || "",
        founding_date: chapter.founding_date || "",
      });
    }
  }, [chapter, form]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveChapter = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/chapters/${id}`, {
        ...form,
        description: form.description || null,
        founding_date: form.founding_date || null,
      });
      toast.success("Chapter updated");
      qc.invalidateQueries({ queryKey: ["admin-chapters"] });
      qc.invalidateQueries({ queryKey: ["admin-chapter", id] });
      setForm(null); // force reload from server
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !form) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;

  return (
    <div data-testid="chapter-edit-page">
      <Link to="/admin/chapters" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]">
        <ArrowLeft size={16} /> Chapters
      </Link>

      <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide mb-8" data-testid="chapter-edit-name">{chapter.name}</h1>

      <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
        <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-3">Chapter Details</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Name</label>
            <input value={form.name} onChange={(e) => update("name", e.target.value)} className="lv-input" data-testid="edit-chapter-name" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">City</label>
            <input value={form.city} onChange={(e) => update("city", e.target.value)} className="lv-input" data-testid="edit-chapter-city" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Country</label>
            <input value={form.country} onChange={(e) => update("country", e.target.value)} className="lv-input" data-testid="edit-chapter-country" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Founding Date</label>
            <input type="date" value={form.founding_date} onChange={(e) => update("founding_date", e.target.value)} className="lv-input" data-testid="edit-chapter-founding-date" />
          </div>
        </div>

        <div>
          <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Description</label>
          <textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="lv-input min-h-[80px]" rows={3} placeholder="Chapter description..." data-testid="edit-chapter-description" />
        </div>

        <div className="text-xs text-lv-muted/60 space-y-1">
          <p>Code: <span className="text-cream/80">{chapter.code}</span></p>
          <p>Status: <span className={chapter.status === "ACTIVE" ? "text-lv-active" : "text-lv-inactive"}>{chapter.status}</span></p>
          {chapter.chapter_leads?.length > 0 && <p>Leads: <span className="text-cream/80">{chapter.chapter_leads.join(", ")}</span></p>}
        </div>

        <button onClick={saveChapter} disabled={saving} className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="save-chapter-btn">
          <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
